import { commonLang } from '@umu/ukit-lang';
/*
 * @Author: zhaihaooran
 * @Date: 2021-07-07 11:58:15
 * @LastEditors: OBKoro1
 * @FilePath: /umu_mobile/src/common/components/@SlidePicker/index.tsx
 */
import React, { useState, useEffect, useCallback } from 'react';
import { DatePicker, PopupModal, IDatePickerProps, IPopupPickerProps } from '@umu/umu-slide-picker';
import { useBoolean } from '@umu/umu-react-hooks';
import { $document, classSet } from '@umu/umu-dom';
import * as UA from '@umu/umu-ua';
import { TimePicker } from './TimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { localeConfig } from './localeConfig';
import {
    WEEK_TEXT_MAP,
    WeekType,
    DATE_FORMAT,
    DATE_NOW,
    PICKER_MODE,
    MODE_SETTING,
    renderVal,
    DATE_INFINITY,
} from './config';
import './index.scss';

export { DATE_INFINITY, DATE_FORMAT, DATE_NOW, PICKER_MODE, renderVal };

interface ISlidePickerBase extends IPopupPickerProps {
    datePickerProps?: IDatePickerProps;
    /**
     * 初始值
     */
    value?: string;
    children?: React.ReactNode;
    /**
     * 是否展示弹窗
     */
    visible: boolean;
    /**
     * 选择初始值
     */
    timeRangeStart?: string;
    /**
     * 日历类型
     */
    mode?: PICKER_MODE;
    /**
     * 关闭
     */
    onClose(): void;
    /**
     * 选中值回调
     */
    onSelect(date?: string): void;
}

/**
 * mobile端 使用 PopupSlidePicker
 * pc端 使用 TimePicker
 */
export function SlidePicker({
    value,
    datePickerProps,
    visible,
    onClose,
    onSelect,
    className = '',
    timeRangeStart = '',
    children,
    mode = PICKER_MODE.DATE,
}: ISlidePickerBase) {
    const defaultValue = dayjs(value ? MODE_SETTING[mode].formatValue(value) : undefined);
    /**
     * 只针对桌面端。兼容pc日历选择弹窗点击其他地方关闭modal逻辑
     */
    useEffect(() => {
        const listener = (event: MouseEvent | TouchEvent) => {
            const dom = visible ? $document.querySelector('.rc-picker-dropdown') : $document.body;
            if (!UA.isDesktop || !dom || dom.contains(event.target as Node)) {
                return;
            }

            onClose();
        };
        if (visible) {
            $document.addEventListener('mousedown', listener);
            $document.addEventListener('touchstart', listener);
        } else {
            $document.removeEventListener('mousedown', listener);
            $document.removeEventListener('touchstart', listener);
        }
        return () => {
            $document.removeEventListener('mousedown', listener);
            $document.removeEventListener('touchstart', listener);
        };
    }, [visible]);

    const disabledDate = useCallback((date: dayjs.Dayjs) => {
        return timeRangeStart && date && date < dayjs(timeRangeStart).startOf('day');
    }, []);

    const onSelectVal = (val: Date | string) => {
        let result;
        if (val instanceof Date) {
            result = dayjs(val).format(MODE_SETTING[mode].format);
        } else {
            result = val;
        }
        onSelect(result);
        /**
         * tip: 针对PM要求，time模式特殊定制的要求
         */
        if (UA.isDesktop && mode === PICKER_MODE.TIME && value !== val) {
            return;
        }
        onClose();
    };

    const renderPicker = () => {
        if (UA.isDesktop) {
            return (
                visible && (
                    <TimePicker
                        className="umu-pc-picker"
                        border={false}
                        readonly={false}
                        initValue={defaultValue}
                        picker={mode}
                        open
                        showNow={false}
                        disabledDate={disabledDate}
                        onSelect={onSelectVal}
                    />
                )
            );
        } else {
            return (
                <PopupModal
                    visible={visible}
                    transitionName="rmc-picker-popup-slide-fade"
                    maskTransitionName="rmc-picker-popup-fade"
                    onDismiss={onClose}
                    onOk={onSelectVal}
                    okText={commonLang('确定')}
                    dismissText={commonLang('取消')}
                    datePicker={
                        <DatePicker
                            minDate={timeRangeStart && dayjs(timeRangeStart).toDate()}
                            className="umu-slide-picker"
                            defaultDate={defaultValue.toDate()}
                            mode={mode}
                            locale={localeConfig.locale}
                            {...datePickerProps}
                        />
                    }
                />
            );
        }
    };

    return (
        <div className={classSet('slide-picker', className)}>
            {children}
            {renderPicker()}
        </div>
    );
}

/**
 * 任务发布器 - 日历组件
 */
interface ISlideTaskPickerType extends Omit<ISlidePickerBase, 'visible' | 'onClose'> {
    titleText: string;
    placeholder: string;
    isShowValue?: boolean;
    onDisabledCallback?(): void;
}

export function SlideTaskPicker({
    titleText = '',
    placeholder,
    isShowValue = true,
    value,
    onDisabledCallback = () => undefined,
    ...res
}: ISlideTaskPickerType) {
    const [visible, { setTrue: setVisibleTrue, setFalse: setVisibleFalse }] = useBoolean(false);

    const onShowPicker = useCallback(() => {
        if (!res.disabled) {
            setVisibleTrue();
        } else {
            onDisabledCallback();
        }
    }, [visible, res.disabled]);

    return (
        <SlidePicker className="form-row-content" {...res} value={value} onClose={setVisibleFalse} visible={visible}>
            <a className="text space-between" onClick={onShowPicker}>
                <span>{titleText}</span>
                {renderVal(isShowValue ? value : '', placeholder, res.disabled)}
            </a>
        </SlidePicker>
    );
}

/**
 * 打卡详情页 - 日历组件
 */
type ISelectType = keyof typeof MODE_PICKER | 'custom';

interface IRowSlideTaskPickerType extends Omit<ISlidePickerBase, 'visible' | 'onClose'> {
    onSelect(val: string, index?: number): void;
    selectType?: ISelectType;
    actIndex?: number;
}
interface IRowPickerItemType {
    week: string;
    number: number;
    val: string;
    isActive: boolean;
}

const getCustom = (actValue: number = 0) => {
    return {
        isActive: actValue,
        weekdays: (val: string) => {
            const days = [];
            for (let i = actValue; i > 0; i--) {
                days.push(dayjs(val).subtract(i, 'day'))
            }

            days.push(dayjs(val))

            for (let i = 1 ; i < 5 - actValue ; i++) {
                days.push(dayjs(val).add(i, 'day'))
            }
            return days;
        },
    }
}

const MODE_PICKER = {
    pick: {
        isActive: 2,
        weekdays: (val: string) => [
            dayjs(val).subtract(2, 'day'),
            dayjs(val).subtract(1, 'day'),
            dayjs(val),
            dayjs(val).add(1, 'day'),
            dayjs(val).add(2, 'day'),
        ],
    },
    default: {
        isActive: 4,
        weekdays: (val: string) => [
            dayjs(val).subtract(4, 'day'),
            dayjs(val).subtract(3, 'day'),
            dayjs(val).subtract(2, 'day'),
            dayjs(val).subtract(1, 'day'),
            dayjs(val),
        ],
    },
};

export function generateRowPickerData(val: string, mode: ISelectType = 'pick', activeDay: number = 0) {
    let data: {
        weekdays: (e: string) => Dayjs[];
        isActive: number;
    };
    if (mode === 'custom') {
        data = getCustom(activeDay);
    } else {
        data = MODE_PICKER[mode];
    }

    const {
        weekdays, isActive
    } = data;

    return weekdays(val).map((v: Dayjs, key: number) => ({
        week: WEEK_TEXT_MAP[v.day() as unknown as WeekType],
        number: v.date(),
        val: v.format(DATE_FORMAT),
        isActive: key === isActive,
    }));
}

export function RowSlideTaskPicker({ value = DATE_NOW, onSelect, selectType = 'default', actIndex, ...res }: IRowSlideTaskPickerType) {
    const [visible, { setTrue: setVisibleTrue, setFalse: setVisibleFalse }] = useBoolean(false);
    const [itemList, setItemList] = useState<IRowPickerItemType[]>(generateRowPickerData(value, selectType, actIndex));

    const onChooseDay = useCallback(
        (val: string, index: number) => {
            const curActiveVal = ((itemList.find((v) => v.isActive) || {}) as IRowPickerItemType).val;
            if (curActiveVal !== val) {
                setItemList(
                    itemList.map((v) => {
                        let isActive = false;
                        if (val === v.val) {
                            isActive = true;
                        }
                        return {
                            ...v,
                            isActive,
                        };
                    }),
                );
            }
            onSelect(val, index);
        },
        [itemList],
    );

    /**
     * 日历选择一个日期后，要重新生成
     * 1. 选中的日期在最中间
     */
    const onPickerSelect = useCallback((val: string) => {
        setItemList(generateRowPickerData(val));
        onSelect(val, 2);
    }, []);

    return (
        <SlidePicker
            className="task-detail-picker"
            {...res}
            onSelect={onPickerSelect}
            value={value}
            onClose={setVisibleFalse}
            visible={visible}
        >
            {itemList.map(({ week, number, val, isActive }, index) => {
                return (
                    <div
                        className={classSet('item', isActive ? 'active' : '')}
                        key={val}
                        onClick={() => onChooseDay(val, index)}
                    >
                        <div className="item-week">{week}</div>
                        <div className="item-number">{number}</div>
                    </div>
                );
            })}
            <div className="item item-picker" onClick={setVisibleTrue}>
                <i className="icon iconfont icon-calendar"></i>
            </div>
        </SlidePicker>
    );
}
